import { maxBy, sumBy } from 'lodash';
import type { MathExpr } from '../../lib/math';
import { mathDiv, mathMul, mathSub, mathVar } from '../../lib/math';
import type { Distances } from '../distances/context';
import type { BestSchedule } from './suggestions/Suggestions';
import type { ScheduleDataContext } from './ScheduleDataContext';
import type { TimeDataTotals, TimeData, Events } from './TimeData';
import {
  getEventStartLocationId,
  getEventEndLocationId,
  isPortCallDepartureEventTimeData,
  isLoadEventTimeData,
  isCargoEventTimeData,
  isCargoEvent,
  isTCOrderEventTimeData,
} from './TimeData';
import type { ScenarioOrder } from '../../lib/planningData';
import { memoize2 } from '@seaber/common/lib/memoize';
import type { GetIncomeForLoadedAmount } from '../shipment/income/income';
import { isEuEtsCostEnabledForUser } from '../shipment/income/income';
import type { TceComputationOptions } from '../shipment/TCE';
import { getCostSharingDetailsForPortCalls } from '../shipment/TCE';
import { getEventShipmentId } from '../shipment/shipment';
import { computeTceForTimeData } from '../shipment/TCE';
import { parseClientId, type ClientId } from '@seaber/common/lib/clientId';
import type { PnlData } from '../hooks/usePnlData';
import { DefaultMap } from '@seaber/common/lib/collections';
import { mapFind, rangesOverlap } from '../../lib/util';
import type { CalculateTimeDataPnl } from '../pnl/ui/context';
import type { PnlProcessorContextInput } from '../pnl/context/context';

const getOrderShortestDistance = memoize2(
  (
    order: Pick<ScenarioOrder, 'loadPortId' | 'dischargePortId'>,
    distances: Distances,
  ): number | null => {
    if (!order.loadPortId) {
      return null;
    }
    return (
      distances.get(order.loadPortId, order.dischargePortId, false)?.dist ??
      null
    );
  },
);

export function countTotalMassDistance(
  timedata: TimeData[],
  distances: Distances,
  /** When normalized, result is divided by total mass. This is useful when evaluating partial solutions while constructing suggestions. */
  normalize?: boolean,
): number {
  let totalMass = 0;
  let massDistance = 0;

  for (const item of timedata) {
    if (isLoadEventTimeData(item)) {
      const distance = getOrderShortestDistance(item.event.order, distances);

      if (distance) {
        const mass = item.mass;
        totalMass += mass;
        massDistance += mass * distance;
      }
    }
  }

  return normalize && totalMass !== 0 ? massDistance / totalMass : massDistance;
}

/** Given a continous sequence of tds, gives the total distance as a sum of all legs between tds */
export function countTotalDistance(tds: TimeData[]): number {
  return sumBy(tds, (td) => td.distance);
}

export function countVoyageCargoPerformance(
  schedule: { totals: TimeDataTotals; timeData: TimeData[] },
  originalMassDistance: number,
  originalDistance: number,
  distances: Distances,
  /** When normalized, total mass distance is divided by total mass. This is useful when evaluating partial solutions while constructing suggestions. */
  normalize?: boolean,
) {
  const totalDistance = schedule.totals.distance;
  const totalMassDistance = countTotalMassDistance(
    schedule.timeData,
    distances,
    normalize,
  );
  return (
    (totalMassDistance - originalMassDistance) /
    Math.max(1, totalDistance - originalDistance)
  );
}

/** Returns the last shipment in the schedule which is within the range of the given orders' events */
function getLastOverlappingShipmentForOrders(
  events: Events[],
  orders: Set<number>,
): ClientId | null {
  const shipmentRanges = new DefaultMap<
    ClientId,
    { start: number; end: number }
  >(() => ({ start: Infinity, end: -Infinity }));
  const newOrderRanges = new DefaultMap<number, { start: number; end: number }>(
    () => ({ start: Infinity, end: -Infinity }),
  );

  events.forEach((event, i) => {
    if (isCargoEvent(event)) {
      if (orders.has(event.order.id)) {
        newOrderRanges.update(event.order.id, (x) => ({
          start: Math.min(x?.start ?? i, i),
          end: Math.max(x?.end ?? i, i),
        }));
      } else {
        const shipmentId = getEventShipmentId(event);
        if (!shipmentId) return;
        shipmentRanges.update(shipmentId, (x) => ({
          start: Math.min(x?.start ?? i, i),
          end: Math.max(x?.end ?? i, i),
        }));
      }
    }
  });

  const overlappingShipments = [...shipmentRanges].filter(
    ([, shipmentRange]) => {
      return !!mapFind(newOrderRanges, (orderRange) =>
        rangesOverlap(shipmentRange, orderRange),
      );
    },
  );

  const lastStartedShipment = maxBy(
    overlappingShipments,
    ([, shipmentRange]) => shipmentRange.start,
  );

  return lastStartedShipment?.[0] ?? null;
}

/** Given a suggestion, returns which shipment the suggestion should be added to.
 * This is based on the last shipment in the original schedule which is overlapped by the new suggestion. */
export function getTargetShipmentForSuggestion(
  combination: BestSchedule['combination'],
): ClientId | null {
  return getLastOverlappingShipmentForOrders(
    combination.schedule,
    new Set(combination.assignments.map((a) => a.order.id)),
  );
}

/** Given a range of tds, expand it to include all tds in the first and last port calls.
 * e.g. timeData = [Arrival, Berthing, Load0, Departure, Arrival, Berthing, Discharge0, Load1, Departure, Arrival, Berthing, Discharge1, Departure],
 *      startIndex = 7 (Load1), endIndex = 11 (Disharge1)
 *   => [4, 11] (includes the Arrival, Berthing, Discharge0 before Load1, and Departure after Discharge1)
 *
 */
export function expandTdRangeToFullPortCalls(
  timeData: TimeData[],
  startIndex: number,
  endIndex: number,
): [number, number] {
  while (
    startIndex > 0 &&
    getEventStartLocationId(timeData[startIndex].event) ===
      getEventEndLocationId(timeData[startIndex].event) &&
    getEventStartLocationId(timeData[startIndex].event) ===
      getEventStartLocationId(timeData[startIndex - 1].event)
  ) {
    startIndex--;
  }
  if (isPortCallDepartureEventTimeData(timeData[startIndex])) {
    startIndex++;
  }
  while (
    endIndex < timeData.length - 1 &&
    getEventStartLocationId(timeData[startIndex].event) ===
      getEventEndLocationId(timeData[startIndex].event) &&
    getEventEndLocationId(timeData[endIndex].event) ===
      getEventEndLocationId(timeData[endIndex + 1].event)
  ) {
    endIndex++;
  }
  return [startIndex, endIndex];
}

export const findShipmentRange = (
  timeData: TimeData[],
  shipmentId: ClientId,
): { start: number; end: number } => {
  return {
    start: timeData.findIndex(
      (td) =>
        isCargoEventTimeData(td) && getEventShipmentId(td.event) === shipmentId,
    ),
    end: timeData.findLastIndex(
      (td) =>
        isCargoEventTimeData(td) && getEventShipmentId(td.event) === shipmentId,
    ),
  };
};

/** Updates the shipmentId on the entries for the given orders. Returns a new array.
 * Used to manipulate the entries from suggestions after we have identified the final shipment to attach to or create. */
const patchOrderShipmentIds = (
  timeData: readonly TimeData[],
  orderIds: Set<number>,
  newShipmentId: ClientId,
): TimeData[] => {
  return timeData.map((td) => {
    if (isTCOrderEventTimeData(td) && orderIds.has(td.event.order.id)) {
      return {
        ...td,
        event: {
          ...td.event,
          entry: {
            ...td.event.entry,
            shipmentId: newShipmentId,
          },
        },
      };
    }
    return td;
  });
};

export const NEW_SUGGESTION_SHIPMENT_ID = parseClientId(
  'NEW_SUGGESTION_SHIPMENT_ID',
);

export function calculateTceRange(
  schedule: NonNullable<BestSchedule>,
  targetShipmentId: ClientId | null,
  range: 'overlapping_indices' | 'overlapping_voyage',
): {
  timeData: TimeData[];
  startIndex: number;
  endIndex: number;
  targetShipmentId: ClientId;
} {
  const assignments = schedule.combination.assignments;
  let { timeData } = schedule;

  const lastIndex = timeData.length - 1;
  /** First Td index included in TCE calculation */
  let startIndex = lastIndex;
  /** Last Td index included in TCE calculation */
  let endIndex = 0;

  if (range === 'overlapping_voyage') {
    targetShipmentId ??= getTargetShipmentForSuggestion(schedule.combination);

    if (targetShipmentId) {
      // Initially set shipment bounds to existing shipment tds
      ({ start: startIndex, end: endIndex } = findShipmentRange(
        timeData,
        targetShipmentId,
      ));
    }
  }

  targetShipmentId ??= NEW_SUGGESTION_SHIPMENT_ID;

  // Until now it is still possible that new suggestion entries do not have a shipmentId. The rest of the code requires a shipmentId.
  timeData = patchOrderShipmentIds(
    timeData,
    new Set(assignments.map((a) => a.order.id)),
    targetShipmentId,
  );

  // Make sure to include all new cargo tds into the range
  assignments.forEach(({ loadIndex, dischargeIndex }) => {
    startIndex = Math.min(loadIndex, startIndex);
    endIndex = Math.max(dischargeIndex, endIndex);
  });

  // Expand the range to include full port calls
  [startIndex, endIndex] = expandTdRangeToFullPortCalls(
    timeData,
    startIndex,
    endIndex,
  );

  return {
    timeData,
    startIndex,
    endIndex,
    targetShipmentId,
  };
}

export function getSuggestionTCEDelta(
  originalTimeData: TimeData[],
  targetShipmentId: ClientId | null,
  schedule: NonNullable<BestSchedule>,
  getIncomeForLoadedAmount: GetIncomeForLoadedAmount,
  calculateTimeDataPnl: CalculateTimeDataPnl,
  context: Pick<ScheduleDataContext, 'distances' | 'currentUser'>,
  options: {
    etsFactor: number;
    outGoingCoaId: number | null;
    carbonPrice: number | null;
    pnlData: PnlData | null;
    vessel: PnlProcessorContextInput['vessel'];
  },
  /** The range of tds to consider into the TCE delta. overlapping_voyage is a superset of overlapping_indices. */
  range: 'overlapping_indices' | 'overlapping_voyage',
  orderById: Map<number, ScenarioOrder> | null,
) {
  const {
    timeData,
    startIndex,
    endIndex,
    targetShipmentId: finalTargetShipmentId,
  } = calculateTceRange(schedule, targetShipmentId, range);

  const assignments = schedule.combination.assignments;

  const originalFirstChangedIndex = startIndex;
  const originalLastChangedIndex = endIndex - assignments.length * 2;

  const tceOptions: Omit<TceComputationOptions, 'lastIndex'> = {
    firstIndex: startIndex,
    euEtsCostEnabled: isEuEtsCostEnabledForUser(context.currentUser),
    fuelEuCostEnabled:
      !!context.currentUser?.rootOrganization?.configIncludeFuelEuInCosts,
    outgoingCoaId: options.outGoingCoaId,
    pnlData: options.pnlData,
    portCallCostSharingDetails: getCostSharingDetailsForPortCalls(timeData),
  };

  const newPnl = calculateTimeDataPnl(timeData, {
    type: 'vessel',
    vessel: options.vessel,
  });

  // Compute TCE for new schedule
  const newTceResult = computeTceForTimeData(
    timeData,
    finalTargetShipmentId,
    getIncomeForLoadedAmount,
    context.distances,
    {
      ...tceOptions,
      lastIndex: endIndex,
    },
    orderById,
    newPnl,
  );

  const originalPnl = calculateTimeDataPnl(originalTimeData, {
    type: 'vessel',
    vessel: options.vessel,
  });

  // Compute TCE for original schedule
  const originalTceResult =
    finalTargetShipmentId &&
    finalTargetShipmentId !== NEW_SUGGESTION_SHIPMENT_ID
      ? computeTceForTimeData(
          originalTimeData,
          finalTargetShipmentId,
          getIncomeForLoadedAmount,
          context.distances,
          {
            ...tceOptions,
            lastIndex: originalTimeData.length - 1,
          },
          orderById,
          originalPnl,
        )
      : null;

  if (!newTceResult) {
    throw new Error('Failed to compute TCE for new schedule');
  }

  return {
    cost: newTceResult.voyageCost,
    totalExtraCosts: newTceResult.totalExtraCosts,
    totalPortCosts: newTceResult.totalPortCost,
    tcCost: newTceResult.tcCost,
    tce: newTceResult.tce,
    durationInDays: newTceResult.durationInDays,
    seaDurationInDays: newTceResult.seaDurationInDays,
    portActivitiesInDays: newTceResult.portActivitiesInDays,
    suggestionVoyageStart: newTceResult.earliestStart,
    suggestionVoyageEnd: newTceResult.latestEnd,
    income: newTceResult.income,
    missingIncomeOrderIds: newTceResult.missingIncomeOrderIds,
    tceDelta:
      originalTceResult && originalFirstChangedIndex < originalLastChangedIndex
        ? newTceResult.tce - originalTceResult?.tce
        : null,
  };
}

export type VoyagePerformanceMetrics = {
  massDistance: number;
  distance: number;
  idealDistance: number;
  totalMass: number;
  vcp: MathExpr;
};

export type SuggestionPerformanceMetrics = {
  originalVoyage: VoyagePerformanceMetrics;
  newVoyage: VoyagePerformanceMetrics;
  delta: VoyagePerformanceMetrics;
};

export const computeVoyagePerformanceMetrics = (
  schedule: TimeData[],
  distances: Distances,
  /** Metric tons */
  massCapacity: number,
): VoyagePerformanceMetrics => {
  const massDistance = mathVar(
    countTotalMassDistance(schedule, distances),
    'sum(massDistance)',
  );
  const totalDistance = mathVar(countTotalDistance(schedule), 'sum(distance)');
  const totalMass = sumBy(
    schedule.filter(isLoadEventTimeData),
    (td) => td.mass,
  );

  const idealDistance = mathDiv(massDistance, massCapacity);

  const vcp = mathMul(mathDiv(idealDistance, totalDistance), 100);

  if (+massDistance === 0) {
    return {
      massDistance: 0,
      distance: 0,
      idealDistance: 0,
      totalMass: 0,
      vcp: mathVar(0),
    };
  }
  return {
    massDistance: +massDistance,
    distance: +totalDistance,
    idealDistance: 0,
    totalMass,
    vcp,
  };
};

export function computeSuggestionPerformanceMetrics(
  originalSchedule: TimeData[],
  newSchedule: TimeData[],
  distances: Distances,
  /** Metric tons */
  massCapacity: number,
): SuggestionPerformanceMetrics {
  const originalMetrics = computeVoyagePerformanceMetrics(
    originalSchedule,
    distances,
    massCapacity,
  );
  const newMetrics = computeVoyagePerformanceMetrics(
    newSchedule,
    distances,
    massCapacity,
  );
  const orderIdsInOriginal = new Set(
    originalSchedule.filter(isLoadEventTimeData).map((td) => td.event.order.id),
  );
  const newOrders = newSchedule
    .filter(isLoadEventTimeData)
    .filter((td) => !orderIdsInOriginal.has(td.event.order.id))
    .map((td) => td.event.order);
  const directDistanceByOrderId = new Map<number, number>(
    newOrders.map((order) => [
      order.id,
      getOrderShortestDistance(order, distances) ?? 0,
    ]),
  );

  const massDistanceDelta = mathVar(
    newMetrics.massDistance - originalMetrics.massDistance,
    'ΔtotalMassDistance',
  );
  const distanceDelta = mathVar(
    newMetrics.distance - originalMetrics.distance,
    'Δdistance',
  );
  const massDelta = newMetrics.totalMass - originalMetrics.totalMass;
  const massCapacityVar = mathVar(massCapacity, 'massCapacity');
  /** Hypothetical distance to travel if the new orders could fill the vessel to 100% be delivered with perfect efficiency. */
  const idealDistance = mathDiv(massDistanceDelta, massCapacityVar);
  /** Lower bound the vessel will at least have to travel to deliver all new cargo */
  const minimumDistance = mathVar(
    Math.max(...directDistanceByOrderId.values(), +idealDistance),
    'minimumDistance',
  );

  /** Max ratio the vessel could be loaded if all new orders carried at once */
  const maxFillRate = mathVar(
    Math.min(+idealDistance / +minimumDistance, 1),
    'maxFillRate',
  );
  const distanceRatio = mathDiv(idealDistance, distanceDelta);

  const vcp = mathMul(
    +distanceRatio < 1
      ? // Added distance at least the ideal distance, use same scoring as for existing voyages
        distanceRatio
      : // For efficiency above 100%, linear score up to 200% (max when distanceDelta is 0)
        mathMul(maxFillRate, mathSub(2, mathDiv(distanceDelta, idealDistance))),
    100,
  );

  const delta: SuggestionPerformanceMetrics['delta'] = {
    massDistance: +massDistanceDelta,
    distance: +distanceDelta,
    idealDistance: +idealDistance,
    totalMass: massDelta,
    vcp,
  };

  return {
    originalVoyage: originalMetrics,
    newVoyage: newMetrics,
    delta,
  };
}
